import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { fetchMe, login as apiLogin, logout as apiLogout, refreshAccessToken, type MeResponse } from "@/lib/api";

type AuthState =
  | { status: "loading"; user: null }
  | { status: "authenticated"; user: MeResponse }
  | { status: "anonymous"; user: null };

type AuthContextValue = AuthState & {
  login: (email: string, password: string) => Promise<MeResponse>;
  logout: () => void;
  refreshUser: () => Promise<MeResponse | null>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading", user: null });

  useEffect(() => {
    // Au boot: si un refresh_token est en sessionStorage, on tente une session
    (async () => {
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        setState({ status: "anonymous", user: null });
        return;
      }
      try {
        const me = await fetchMe();
        setState({ status: "authenticated", user: me });
      } catch {
        setState({ status: "anonymous", user: null });
      }
    })();
  }, []);

  const login = async (email: string, password: string): Promise<MeResponse> => {
    await apiLogin(email, password);
    const me = await fetchMe();
    setState({ status: "authenticated", user: me });
    return me;
  };

  const logout = () => {
    apiLogout();
    setState({ status: "anonymous", user: null });
  };

  const refreshUser = async (): Promise<MeResponse | null> => {
    try {
      const me = await fetchMe();
      setState({ status: "authenticated", user: me });
      return me;
    } catch {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
